import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CheckCircle2,
  CircleAlert,
  X,
} from "lucide-react";
import type {
  CoachingClip,
  CoachingNote,
  CoachingPlaylist,
  Match,
  Player,
} from "@/lib/types";
import { HlsVideo } from "./HlsVideo";
import { getStreamUrls, errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Badge } from "@/components/ui/Badge";
import { useClipBoundaryWatcher } from "./useClipBoundaryWatcher";
import { useTelestrationReplay } from "./useTelestrationReplay";
import { useVodHeartbeat } from "./useVodHeartbeat";
import { formatPlayhead, useVideoPlayhead } from "./useVideoPlayhead";
import { usePlaylistSession } from "./usePlaylistSession";
import { PlaylistSessionRail } from "./PlaylistSessionRail";
import { playlistFallbackCopy } from "./playlistFallbackCopy";
import { TelestratorCanvas } from "@/features/canvas/TelestratorCanvas";
import { loadDrawing, emptyDrawing } from "@/lib/canvas/telestrator-schema";
import {
  ThumbnailDrawingOverlay,
  VideoNoteThumbnail,
} from "@/features/coach-review/components/VideoNoteThumbnail";
import { UnifiedFeedbackBody } from "@/features/feedback/components/UnifiedFeedbackBody";
import { useFeedbackReview } from "@/features/feedback/hooks/useFeedbackReview";
import { PlayerResponseChips } from "@/features/feedback/components/PlayerResponseChips";
import type { PlayerResponseIndex } from "@/features/feedback/responseSignals";
import type { PlayerResponseTarget } from "@/features/feedback/components/PlayerResponseChips";
import { TacticalBoardView } from "@/features/canvas/TacticalBoardView";
import { useCanvasEnvelope } from "@/features/canvas/useCanvasEnvelope";
import { validateScene } from "@/lib/canvas/board-schema";
import { readReferenceVideo } from "@/features/matches/referenceVideo";
import { ReferenceVideoCard } from "@/features/feedback/components/ReferenceVideoCard";
import {
  categoryLabel,
  contextPillLabel,
} from "@/features/feedback/feedbackHelpers";
import { FeedbackTonePill } from "@/features/feedback/components/FeedbackCardBits";
import { ViewerClipControls } from "./ViewerClipControls";

/**
 * Focused feedback player modal — M5 coach-side scope.
 *
 * Ports the legacy focused feedback player
 * (`js/coaching/feedback-player.js`). M5's locked scope is
 * **coach-side preview only** — Coach Review opens this to preview
 * a clip / note / playlist it's working with. The `/feedback`
 * VIEWER entry points are M7.
 *
 * Three modes, discriminated on the `target` prop:
 *
 * **Clip mode** — a bare `<HlsVideo>` constrained to the clip's
 * `[start, end]` window by `useClipBoundaryWatcher`, with a clip-local
 * transport whose full seek bar represents only that saved duration.
 *
 * **Note mode** — a static marked-frame snapshot. Mark Moment notes
 * are not clips; players review the saved frame + telestration as an
 * image-like artifact rather than playing the whole match video.
 *
 * **Playlist mode** — `usePlaylistSession` turns the playlist's
 * ordered notes into a guided session; the modal renders the
 * current item through the SAME note-mode body (keyed on
 * `session.playKey` so each advance / restart remounts cleanly),
 * plus a `<PlaylistSessionRail>` (Prev / Pause / Restart / Next +
 * item info). The session's advance monitor watches the modal's
 * `<video>` and moves to the next item once the post-roll window
 * elapses.
 *
 * Close: backdrop click, Escape, or the X button.
 */

export type FeedbackPlayerTarget =
  | { kind: "clip"; clip: CoachingClip }
  | { kind: "note"; note: CoachingNote }
  | { kind: "playlist"; playlist: CoachingPlaylist };

export type FeedbackPlayerModalProps = {
  /** What to play — a clip, a coaching note, or a playlist. */
  target: FeedbackPlayerTarget;
  /** The match the clip/note belongs to — needed to build stream
   * URLs (`getStreamUrls` keys off `match.team_id`).
   *
   * For clip + note mode this IS the playing match. For PLAYLIST
   * mode each item is a note carrying its own `match_id` +
   * `team_id` (a playlist can span matches), so the player
   * resolves the effective match from `session.currentItem` and
   * advances it as the session does — this prop is just the
   * fallback when there's no current item. The M6a Playlists
   * page passes the playlist's first item's match as that
   * fallback. */
  match: Pick<Match, "id" | "team_id"> & Partial<Pick<Match,"slug">>;
  /** Close handler — backdrop click / Escape / the X button. */
  onClose: () => void;
  /** Linked-player roster used by the unified body to render
   * "For: ⟨player⟩" chips. Optional — when omitted the chip row
   * simply renders ids as "Linked player". The viewer Feedback
   * tabs already hold the bundle, so they pass `bundle.players`;
   * the coach-side preview entry points can leave it undefined. */
  players?: Player[];
  /** The set of note ids the viewer has already marked done.
   * Drives the "Done ✓" chip + the Mark done button
   * affordance. Optional — when omitted, treated as empty. */
  reviewedNoteIds?: Set<number>;
  /** Same as `reviewedNoteIds` but for playlists. */
  reviewedPlaylistIds?: Set<number>;
  /** Same as `reviewedNoteIds` but for coaching clips. */
  reviewedClipIds?: Set<number>;
  /** PR 6: the player's response-signal index (built from the
   * `/api/my-feedback` bundle's `reviews[]`). When present, the
   * modal mounts the three-chip response affordance below the
   * Mark reviewed footer for notes, clips, and playlists. Coach-side
   * preview entry points (Coach Review) omit this — the chips
   * are a player-facing surface. */
  playerResponses?: PlayerResponseIndex;
  /** Coach previews use the approved compact two-column detail dialog.
   * Viewer surfaces keep the playback-first presentation and response tools. */
  presentation?: "viewer" | "coach";
  /** Human-readable match label shown below the saved frame in coach mode. */
  matchLabel?: string;
  /** Coach-only footer actions. Omitted actions are not rendered. */
  onEdit?: () => void;
  onCreateGoal?: () => void;
  /** Called after a viewer marks this item reviewed or updates their
   * response. Review feeds use it to keep the acted-on card on the
   * current All page while the refreshed status is applied. */
  onReviewUpdated?: () => void;
};

export function FeedbackPlayerModal({
  target,
  match,
  onClose,
  players,
  reviewedNoteIds,
  reviewedPlaylistIds,
  reviewedClipIds,
  playerResponses,
  presentation = "viewer",
  matchLabel,
  onEdit,
  onCreateGoal,
  onReviewUpdated,
}: FeedbackPlayerModalProps) {
  const toast = useToast();
  // The element is tracked in STATE — the video primitives forward
  // it up via a callback ref, and the watcher / heartbeat /
  // telestration-replay / playlist-advance hooks read
  // `videoRef.current` and need a re-render to re-arm when it
  // mounts. Same pattern as Coach Review's playhead wiring.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  videoElRef.current = videoEl;

  const [streamKilled, setStreamKilled] = useState(false);

  // Playlist session — `null` for non-playlist targets (the hook
  // is mounted unconditionally; passing `null` yields a null
  // session and no advance monitor).
  const playlist = target.kind === "playlist" ? target.playlist : null;
  const playlistControls = usePlaylistSession(playlist, videoElRef);
  const { session, finished, hasItems, reset } = playlistControls;

  // Resolve the "current playable item" — a note for note +
  // playlist mode, the clip for clip mode.
  const isClip = target.kind === "clip";
  const currentNote: CoachingNote | null =
    target.kind === "note" ? target.note : (session?.currentItem ?? null);

  const slot = isClip ? target.clip.slot : (currentNote?.slot ?? "full");

  // The match whose video backs the player RIGHT NOW. For clip +
  // note mode it's the `match` prop. For PLAYLIST mode each item
  // is a note carrying its OWN `match_id` + `team_id` — a real
  // playlist can span matches — so the effective match is derived
  // from `session.currentItem`, advancing as the session does.
  // Falls back to the `match` prop when there's no current item
  // (the M5 single-match assumption is now a fallback, not a
  // limitation).
  const effectiveMatch: Pick<Match, "id" | "team_id"> =
    target.kind === "playlist" && currentNote
      ? { id: currentNote.match_id ?? match.id, team_id: currentNote.team_id }
      : match;

  // Clip mode: constrain playback to the window. Disabled
  // otherwise (the hook is gated by `enabled` so hook order stays
  // stable).
  useClipBoundaryWatcher({
    videoRef: videoElRef,
    start: isClip ? target.clip.start_seconds : 0,
    end: isClip ? target.clip.end_seconds : 1,
    enabled: isClip,
  });
  const clipPlayhead = useVideoPlayhead(videoElRef);

  const isObservationNote =
    target.kind === "note" &&
    (target.note.note_context ?? "video") === "observation";
  const isSingleVideoNote = target.kind === "note" && !isObservationNote;

  // Playlist mode still uses video playback; single Mark Moment
  // notes render as static snapshots below.
  const noteTimestamp = Number(currentNote?.timestamp_seconds ?? 0);
  const telestrationVisible = useTelestrationReplay({
    videoRef: videoElRef,
    timestampSeconds: noteTimestamp,
    enabled: !isClip && !isSingleVideoNote && currentNote != null,
  });

  // Memoized: `loadDrawing` mints fresh in-memory ids on every call
  // (persisted drawings are id-less), and the modal re-renders ~4×/s
  // on playhead updates — un-memoized, every object's React key would
  // change per render and react-konva would destroy + recreate every
  // canvas node during playback.
  const rawNoteDrawing = currentNote?.drawing ?? null;
  const noteDrawing = useMemo(
    () => loadDrawing(rawNoteDrawing),
    [rawNoteDrawing],
  );
  const displayedDrawing = telestrationVisible ? noteDrawing : emptyDrawing();
  const rawClipDrawing = isClip ? target.clip.drawing : null;
  const clipDrawing = useMemo(
    () => loadDrawing(rawClipDrawing),
    [rawClipDrawing],
  );
  const clipStart = isClip ? Math.max(0, Number(target.clip.start_seconds)) : 0;
  const clipDrawingVisible =
    isClip &&
    clipDrawing.objects.length > 0 &&
    clipPlayhead.paused &&
    (clipPlayhead.currentTime == null ||
      Math.abs(clipPlayhead.currentTime - clipStart) <= 0.5);

  // Clip mode runs its own heartbeat here; note + playlist mode's
  // heartbeat is owned by the `<TelestratorCanvas>` below. Gating
  // by `enabled` keeps exactly one heartbeat per mode.
  useVodHeartbeat({
    matchId: match.id,
    slot,
    videoRef: videoElRef,
    onKilled: () => setStreamKilled(true),
    enabled: isClip,
  });

  // Playlist mode: pause/resume the `<video>` when the session's
  // `paused` flag toggles (the advance monitor is gated inside the
  // hook; this gates the actual element).
  useEffect(() => {
    if (!session) return;
    const video = videoElRef.current;
    if (!video) return;
    if (session.paused) {
      video.pause();
    } else {
      void video.play().catch(() => {
        /* autoplay can be blocked — harmless, the coach presses play */
      });
    }
  }, [session]);

  // Escape closes. Doesn't preventDefault — harmless if another
  // handler also reacts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Observation notes have no playable video — period. The video
  // slot is replaced by the saved tactical board when one exists
  // (the coach's tokens + shapes ARE the drawing for observations),
  // otherwise the slot is omitted entirely and the structured body
  // does the talking. Mirrors legacy `coaching.js:5468-5483`.
  const observationScene = useMemo(() => {
    if (!isObservationNote) return null;
    const raw = (target as { note: CoachingNote }).note
      .tactical_board_json as unknown;
    if (raw == null) return null;
    const result = validateScene(raw);
    if (!result.ok) return null;
    const s = result.value;
    if (s.tokens.length === 0 && s.shapes.length === 0) return null;
    return s;
  }, [isObservationNote, target]);

  // Size the panel to what it actually holds.
  //
  // 1280px was set in #58 for VIDEO and CANVAS content — a clip, a
  // telestration, a tactical board all need that room. But an observation
  // note with no saved board renders no media slot at all (see the
  // `observationScene ? … : null` branch below), so the same 1280px shell
  // wraps a single column of prose: short lines stranded in a very wide box
  // with nothing to the right of them.
  //
  // Text-only reads at ~720px. Anything with media keeps the original width.
  const hasWideMedia = !isObservationNote || observationScene != null;
  const panelWidth = hasWideMedia
    ? "min(100vw - 2rem, 1280px)"
    : "min(100vw - 2rem, 720px)";

  // Mark-reviewed mutation — the backend stores one review row for
  // notes, clips, playlists, and match summaries. Summaries render
  // inline outside this modal, so the modal handles the first three.
  // The mutation already invalidates `["my-feedback"]` so the parent
  // card list also flips its Reviewed pill on success.
  const review = useFeedbackReview();
  const reviewableId: {
    kind: "note" | "clip" | "playlist";
    id: number;
  } | null =
    target.kind === "note"
      ? { kind: "note", id: target.note.id }
      : target.kind === "clip"
        ? { kind: "clip", id: target.clip.id }
        : target.kind === "playlist"
          ? { kind: "playlist", id: target.playlist.id }
          : null;
  const alreadyReviewed =
    reviewableId?.kind === "note"
      ? !!reviewedNoteIds?.has(reviewableId.id)
      : reviewableId?.kind === "clip"
        ? !!reviewedClipIds?.has(reviewableId.id)
        : reviewableId?.kind === "playlist"
          ? !!reviewedPlaylistIds?.has(reviewableId.id)
          : false;
  // Local optimistic flip — the bundle invalidation will eventually
  // reach the parent, but we want the chip to update IMMEDIATELY
  // without closing the modal (per the approved UX call).
  const [locallyReviewed, setLocallyReviewed] = useState(false);
  const showReviewedChip = alreadyReviewed || locallyReviewed;
  const handleMarkReviewed = async () => {
    if (!reviewableId || review.isPending || showReviewedChip) return;
    try {
      if (reviewableId.kind === "note") {
        await review.markReviewed({ note_id: reviewableId.id });
      } else if (reviewableId.kind === "clip") {
        await review.markReviewed({ clip_id: reviewableId.id });
      } else {
        await review.markReviewed({ playlist_id: reviewableId.id });
      }
      setLocallyReviewed(true);
      onReviewUpdated?.();
      toast.success("Marked reviewed");
    } catch (err) {
      /* error surfaced via mutation.error below */
      toast.error("Couldn't mark reviewed", {
        description: errorMessage(err),
      });
    }
  };

  // Canvas envelope — the envelope's *size* is owned by CSS
  // (`aspect-ratio: 16/9` + a `max-height` cap that keeps it fully
  // visible inside the modal). The hook just READS the resolved size
  // and converts it into pixel dimensions for the Konva-backed
  // stages, which need numeric width/height props. This split makes
  // the read/write loop impossible by construction: the envelope's
  // dimensions are determined by the browser's layout, not by
  // anything this hook computes.
  //
  // The max-height budget reserves room for non-canvas modal rows
  // (header, structured body, footer, paddings, gaps) so the canvas
  // and body together fit in 95vh on a standard laptop. Tune the
  // 14rem budget if the non-media chrome grows. The body may scroll
  // below the media on small screens; the video/board should still
  // claim the largest 16:9 stage the viewport can reasonably fit.
  //
  // Both `maxHeight` and `maxWidth` are set from the same budget so
  // the envelope stays a true 16:9 rectangle even when the height
  // cap wins. With only `maxHeight`, CSS `aspect-ratio` is a soft
  // constraint that the clamp overrides — the div would measure as
  // e.g. `1000×400` instead of `711×400`, stretching the video and
  // letting the telestrator overflow `overflow-hidden`.
  const ENVELOPE_HEIGHT_CAP = "min(74dvh, calc(100dvh - 14rem))";
  const envelope = useCanvasEnvelope();
  const ENVELOPE_STYLE = {
    aspectRatio: "16 / 9",
    maxHeight: ENVELOPE_HEIGHT_CAP,
    maxWidth: `calc(${ENVELOPE_HEIGHT_CAP} * 16 / 9)`,
    width: "100%",
  } as const;

  if (typeof document === "undefined") return null;

  // A playlist target whose session resolved to `null` (empty
  // playlist, or finished) — render an accurate empty/complete card
  // rather than a broken player. PR 4 of the redesign distinguishes
  // the two so the completion state can prompt "Review again" /
  // "Back to feedback" instead of leaving the player blank.
  const playlistEmpty = target.kind === "playlist" && !session;
  const playlistKind: "finished" | "empty" =
    hasItems && finished ? "finished" : "empty";
  const playlistCopy =
    target.kind === "playlist"
      ? playlistFallbackCopy(target.playlist.items?.length ?? 0, playlistKind)
      : null;

  const { hlsUrl, mp4Url } = getStreamUrls(effectiveMatch, slot);
  const responseTarget: PlayerResponseTarget | null =
    reviewableId?.kind === "note"
      ? { kind: "note", noteId: reviewableId.id }
      : reviewableId?.kind === "clip"
        ? { kind: "clip", clipId: reviewableId.id }
        : reviewableId?.kind === "playlist"
          ? { kind: "playlist", playlistId: reviewableId.id }
          : null;
  const currentResponse =
    reviewableId?.kind === "note"
      ? playerResponses?.forNote(reviewableId.id)
      : reviewableId?.kind === "clip"
        ? playerResponses?.forClip(reviewableId.id)
        : reviewableId?.kind === "playlist"
          ? playerResponses?.forPlaylist(reviewableId.id)
          : null;

  if (presentation === "coach" && target.kind !== "playlist") {
    const coachMedia = isClip ? (
      <div className="coach-feedback-media-frame relative overflow-hidden bg-black">
        <HlsVideo
          ref={setVideoEl}
          hlsUrl={hlsUrl}
          mp4Url={mp4Url}
          controls={false}
          className="h-full w-full"
          data-testid="feedback-player-video"
        />
        {clipDrawingVisible ? (
          <ThumbnailDrawingOverlay
            overlayId={`player-clip-${target.clip.id}`}
            objects={clipDrawing.objects}
            testId="feedback-player-clip-drawing-overlay"
            scale="full"
          />
        ) : null}
        <span className="coach-feedback-frame-label">
          Clip · {formatPlayhead(target.clip.start_seconds)}–
          {formatPlayhead(target.clip.end_seconds)}
        </span>
      </div>
    ) : isObservationNote ? (
      observationScene ? (
        <div className="coach-feedback-media-frame flex items-center justify-center overflow-hidden border border-rule bg-canvas">
          <TacticalBoardView
            appearance="navy"
            scene={observationScene}
            width={536}
          />
        </div>
      ) : (
        <CoachFeedbackMediaPlaceholder label="No saved board" />
      )
    ) : currentNote?.has_thumbnail === false ? (
      <CoachFeedbackMediaPlaceholder label="No saved frame" />
    ) : currentNote ? (
      <div
        data-testid="feedback-player-note-snapshot"
        className="coach-feedback-media-frame overflow-hidden bg-black"
      >
        <VideoNoteThumbnail
          note={currentNote}
          testId="feedback-player-note-snapshot-image"
          placeholderTestId="feedback-player-note-snapshot-placeholder"
          overlayTestId="feedback-player-note-snapshot-overlay"
          className="relative block h-full w-full overflow-hidden bg-gradient-to-br from-canvas to-surface"
          imgClassName="block h-full w-full object-cover"
          overlayScale="full"
        />
        <span className="coach-feedback-frame-label">
          Saved frame · {formatPlayhead(noteTimestamp)}
        </span>
      </div>
    ) : null;

    return createPortal(
      <div
        className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4"
        data-testid="feedback-player-modal"
        data-presentation="coach"
        data-coach-review-portal="true"
        role="dialog"
        aria-modal="true"
        aria-label={isClip ? "Clip preview" : "Note preview"}
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <section className="coach-feedback-dialog flex max-h-[calc(100dvh-2rem)] w-full max-w-[1120px] flex-col overflow-y-auto rounded border border-rule bg-surface shadow-pop">
          <header className="flex min-h-[70px] items-center justify-between gap-4 border-b border-rule px-4 py-3">
            <h2 className="font-display text-[22px] leading-tight text-ink">
              {isClip
                ? target.clip.title || "Coaching clip"
                : currentNote?.title || "Coaching note"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              data-testid="feedback-player-close"
              aria-label="Close"
              className="flex h-10 w-10 shrink-0 items-center justify-center text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X size={22} aria-hidden />
            </button>
          </header>
          <div className="coach-feedback-dialog-body grid gap-4 p-4 lg:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-3">
              {coachMedia}
              {isClip ? (
                <ViewerClipControls
                  video={videoEl}
                  start={target.clip.start_seconds}
                  end={target.clip.end_seconds}
                  playhead={clipPlayhead}
                />
              ) : null}
              <p className="text-sm text-ink-muted">
                {matchLabel || "Saved coaching feedback"}
                {slot ? ` · ${coachSlotLabel(slot)}` : ""}
              </p>
            </div>
            <CoachFeedbackDetail
              target={target}
              players={players ?? []}
            />
          </div>
          {onEdit || onCreateGoal ? (
            <footer className="flex min-h-[72px] items-center justify-end gap-2 border-t border-rule px-4 py-3">
              {onEdit ? (
                <button
                  type="button"
                  onClick={onEdit}
                  className="rounded bg-accent px-5 py-3 font-display text-sm text-accent-ink transition-colors hover:bg-accent-hover"
                >
                  Edit
                </button>
              ) : null}
              {onCreateGoal ? (
                <button
                  type="button"
                  onClick={onCreateGoal}
                  className="rounded bg-field px-5 py-3 font-display text-sm text-ink transition-colors hover:bg-elevated"
                >
                  New goal
                </button>
              ) : null}
            </footer>
          ) : null}
        </section>
      </div>,
      document.body,
    );
  }

  const title = isClip
    ? target.clip.title || "Coaching Clip"
    : target.kind === "playlist"
      ? target.playlist.title || "Review Session"
      : (target as { note: CoachingNote }).note.title || "Coaching Note";
  const meta = isClip
    ? `Clip · ${target.clip.category} · ${formatPlayhead(
        target.clip.start_seconds,
      )}–${formatPlayhead(target.clip.end_seconds)}`
    : target.kind === "playlist"
      ? session
        ? `Review Session · item ${session.index + 1} of ${session.total}`
        : "Review Session"
      : `Note · ${currentNote?.category ?? "note"} · ${formatPlayhead(
          noteTimestamp,
        )}`;

  if (target.kind !== "playlist") {
    const viewerItem = target.kind === "clip" ? target.clip : target.note;
    const viewerPlayerNames = (players ?? [])
      .filter((player) => viewerItem.player_ids?.includes(player.id))
      .map((player) => player.display_name);
    const viewerCategory = categoryLabel(viewerItem.category);
    const dialogMedia = isClip ? (
      <div
        ref={envelope.setRef}
        className="feedback-viewer-media relative overflow-hidden bg-black"
        data-testid="feedback-player-media"
      >
        <HlsVideo
          ref={setVideoEl}
          hlsUrl={hlsUrl}
          mp4Url={mp4Url}
          controls={false}
          className="h-full w-full"
          data-testid="feedback-player-video"
        />
        {clipDrawingVisible ? (
          <ThumbnailDrawingOverlay
            overlayId={`player-clip-${target.clip.id}`}
            objects={clipDrawing.objects}
            testId="feedback-player-clip-drawing-overlay"
            scale="full"
          />
        ) : null}
        <span className="feedback-viewer-media-label">
          {formatPlayhead(target.clip.start_seconds)}–
          {formatPlayhead(target.clip.end_seconds)} · Saved clip
        </span>
      </div>
    ) : isObservationNote ? (
      observationScene ? (
        <div
          ref={envelope.setRef}
          className="feedback-viewer-media flex items-center justify-center overflow-hidden bg-canvas"
          data-testid="feedback-player-board"
        >
          <TacticalBoardView
            appearance="navy"
            scene={observationScene}
            width={envelope.boardWidth}
          />
          <span className="feedback-viewer-media-label">
            Saved tactical board
          </span>
        </div>
      ) : (
        <CoachFeedbackMediaPlaceholder label="No saved board" />
      )
    ) : currentNote ? (
      <div
        ref={envelope.setRef}
        data-testid="feedback-player-note-snapshot"
        className="feedback-viewer-media overflow-hidden bg-black"
      >
        <VideoNoteThumbnail
          note={currentNote}
          testId="feedback-player-note-snapshot-image"
          placeholderTestId="feedback-player-note-snapshot-placeholder"
          overlayTestId="feedback-player-note-snapshot-overlay"
          className="relative block h-full w-full overflow-hidden bg-gradient-to-br from-canvas to-surface"
          imgClassName="block h-full w-full object-cover"
          overlayScale="full"
        />
        <span className="feedback-viewer-media-label">
          {formatPlayhead(noteTimestamp)} · Saved frame
        </span>
      </div>
    ) : null;

    return createPortal(
      <div
        className="feedback-viewer-dialog-backdrop"
        data-testid="feedback-player-modal"
        data-coach-review-portal="true"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <section className="feedback-viewer-dialog">
          <header>
            <h2>{title}</h2>
            <button
              type="button"
              onClick={onClose}
              data-testid="feedback-player-close"
              aria-label="Close dialog"
            >
              <X size={22} aria-hidden />
            </button>
          </header>

          {streamKilled ? (
            <p
              className="feedback-viewer-error"
              data-testid="feedback-player-stream-killed-error"
            >
              This stream was disconnected by an administrator.
            </p>
          ) : null}

          <div className="feedback-viewer-dialog-body">
            <div className="feedback-viewer-context">
              <span className="feedback-viewer-context-pill">
                {target.kind === "clip"
                  ? "Coaching clip"
                  : contextPillLabel(target.note)}
              </span>
              {target.kind === "note" ? (
                <FeedbackTonePill noteType={target.note.note_type} />
              ) : null}
              {viewerCategory ? (
                <span className="feedback-viewer-context-pill">
                  {viewerCategory}
                </span>
              ) : null}
              <span>{meta}</span>
              {viewerPlayerNames.length > 0 ? (
                <span>For {viewerPlayerNames.join(" & ")}</span>
              ) : null}
            </div>
            <div className="feedback-viewer-media-column">
              {dialogMedia}
              {isClip ? (
                <ViewerClipControls
                  video={videoEl}
                  start={target.clip.start_seconds}
                  end={target.clip.end_seconds}
                  playhead={clipPlayhead}
                />
              ) : null}
              <PlayerActionPreview
                target={target}
                currentNote={currentNote}
                isObservation={isObservationNote && !!observationScene}
              />
            </div>
            <div className="feedback-viewer-copy-column">
              {target.kind === "clip" ? (
                <UnifiedFeedbackBody
                  kind="clip"
                  clip={target.clip}
                  players={players}
                  hideMeta
                />
              ) : currentNote ? (
                <UnifiedFeedbackBody
                  kind="note"
                  note={currentNote}
                  match={match}
                  players={players}
                  reviewed={showReviewedChip}
                  hideMeta
                />
              ) : null}
              {playerResponses && responseTarget ? (
                <PlayerResponseChips
                  target={responseTarget}
                  currentSignal={currentResponse?.signal ?? null}
                  currentNote={currentResponse?.note ?? ""}
                  acknowledgedAt={currentResponse?.acknowledgedAt ?? null}
                  onResponded={onReviewUpdated}
                />
              ) : null}
            </div>
          </div>

          <footer>
            {review.error ? (
              <span
                className="feedback-viewer-error"
                data-testid="feedback-player-review-error"
              >
                Could not save — try again.
              </span>
            ) : null}
            {showReviewedChip ? (
              <span
                data-testid="feedback-player-reviewed-chip"
                className="feedback-viewer-done"
                aria-disabled="true"
              >
                Done ✓
              </span>
            ) : (
              <button
                type="button"
                onClick={handleMarkReviewed}
                disabled={review.isPending}
                data-testid="feedback-player-mark-reviewed"
                className="feedback-viewer-done"
              >
                {review.isPending ? "Saving…" : "Mark done"}
              </button>
            )}
            <button type="button" onClick={onClose}>
              Close
            </button>
          </footer>
        </section>
      </div>,
      document.body,
    );
  }

  // The remaining branch is the legacy guided playlist reader. Keep a
  // union-typed alias because its existing render ladder is shared with
  // older playlist sessions and still contains exhaustiveness guards for
  // note/clip items.
  const legacyTarget = target as FeedbackPlayerTarget;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/80 p-4"
      data-testid="feedback-player-modal"
      data-coach-review-portal="true"
      role="dialog"
      aria-modal="true"
      aria-label={
        isClip
          ? "Clip preview"
          : target.kind === "playlist"
            ? "Review session"
            : "Note preview"
      }
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex w-full max-w-none max-h-[95vh] flex-col gap-3 overflow-y-auto rounded border border-rule bg-surface p-4"
        style={{ width: panelWidth }}
      >
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {/* Tactical Board Review chip — observation note with a
               * non-empty board. Mockup PR 4 Phase 5 calls out this
               * label so the surface never implies video playback
               * when there is none. */}
              {isObservationNote && observationScene ? (
                <span
                  className="text-[0.6rem] font-display uppercase tracking-wider text-accent-ink bg-accent rounded-full px-2 py-0.5"
                  data-testid="feedback-player-tactical-chip"
                >
                  Tactical Board Review
                </span>
              ) : null}
              <h2 className="text-lg font-display tracking-tight text-ink truncate">
                {title}
              </h2>
            </div>
            <p className="text-ink-muted text-xs font-mono mt-0.5">{meta}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="feedback-player-close"
            aria-label="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center border-0 bg-transparent text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X size={22} aria-hidden />
          </button>
        </header>

        {streamKilled ? (
          <p
            className="text-destructive font-mono text-sm"
            data-testid="feedback-player-stream-killed-error"
          >
            This stream was disconnected by an administrator.
          </p>
        ) : null}

        {playlistEmpty ? (
          <div
            className="py-10 text-center"
            data-testid="feedback-player-playlist-empty"
            data-playlist-state={playlistKind}
          >
            {/* Completion icon — green check on finished, neutral on empty. */}
            <div
              className={[
                "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full",
                playlistKind === "finished"
                  ? "bg-good/15 text-good"
                  : "bg-field text-ink-muted",
              ].join(" ")}
              aria-hidden="true"
            >
              {playlistKind === "finished" ? (
                <CheckCircle2 size={28} strokeWidth={2.5} aria-hidden="true" />
              ) : (
                <CircleAlert size={28} strokeWidth={2.5} aria-hidden="true" />
              )}
            </div>
            <p
              className="font-display text-lg text-ink"
              data-testid="feedback-player-playlist-empty-title"
            >
              {playlistCopy?.title}
            </p>
            <p className="mt-1 text-sm text-ink-muted max-w-md mx-auto">
              {playlistCopy?.body}
            </p>
            <div
              className="mt-5 flex flex-wrap items-center justify-center gap-2"
              data-testid="feedback-player-playlist-actions"
            >
              {playlistKind === "finished" ? (
                <button
                  type="button"
                  onClick={reset}
                  data-testid="feedback-player-review-again"
                  className="rounded border border-rule bg-canvas px-3 py-1.5 text-sm font-display text-ink transition-colors hover:bg-field"
                >
                  Review again
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                data-testid="feedback-player-back-to-feedback"
                className="rounded bg-accent px-3 py-1.5 text-sm font-display font-semibold text-accent-ink hover:bg-accent-hover transition-colors"
              >
                Back to feedback
              </button>
            </div>
          </div>
        ) : isClip ? (
          /* Clip mode — video controls stay native, but the saved
           * clip drawing is pinned to the first frame. It disappears
           * as soon as playback starts or the playhead leaves IN. */
          <div
            ref={envelope.setRef}
            className="relative shrink-0 overflow-hidden rounded bg-black mx-auto"
            style={ENVELOPE_STYLE}
          >
            <HlsVideo
              ref={setVideoEl}
              hlsUrl={hlsUrl}
              mp4Url={mp4Url}
              controls
              className="h-full w-full"
              data-testid="feedback-player-video"
            />
            {clipDrawingVisible ? (
              <ThumbnailDrawingOverlay
                overlayId={`player-clip-${(
                  legacyTarget as Extract<FeedbackPlayerTarget, { kind: "clip" }>
                ).clip.id}`}
                objects={clipDrawing.objects}
                testId="feedback-player-clip-drawing-overlay"
                scale="full"
              />
            ) : null}
          </div>
        ) : isObservationNote ? (
          /* Observation note — NEVER show video. Render the saved
           * tactical board when one exists; otherwise omit the
           * canvas slot entirely and let the structured body do
           * the talking. The board is centered inside a 16:9
           * envelope (matching the clip/video frame for visual
           * consistency) and fit by envelope height — pitch
           * aspect (~1.544:1) is narrower than 16:9 (~1.778:1),
           * so horizontal padding appears on the sides and the
           * outer pitch lines that previously sat at the canvas
           * edge become visible. */
          observationScene ? (
            <div
              ref={envelope.setRef}
              className="mx-auto flex shrink-0 items-center justify-center overflow-hidden rounded border border-rule bg-canvas"
              style={ENVELOPE_STYLE}
              data-testid="feedback-player-board"
            >
              <TacticalBoardView
                appearance="navy"
                scene={observationScene}
                width={envelope.boardWidth}
              />
            </div>
          ) : null
        ) : isSingleVideoNote && currentNote ? (
          /* Mark Moment note — static frame + telestration, not a
           * playable video. Clips remain the moving-video surface. */
          <div
            ref={envelope.setRef}
            data-testid="feedback-player-note-snapshot"
            className="shrink-0 overflow-hidden rounded bg-black mx-auto"
            style={ENVELOPE_STYLE}
          >
            <VideoNoteThumbnail
              note={currentNote}
              testId="feedback-player-note-snapshot-image"
              placeholderTestId="feedback-player-note-snapshot-placeholder"
              overlayTestId="feedback-player-note-snapshot-overlay"
              className="relative block h-full w-full overflow-hidden bg-gradient-to-br from-canvas to-surface"
              imgClassName="block h-full w-full object-cover"
              overlayScale="full"
            />
          </div>
        ) : (
          /* Playlist mode — the telestrator canvas hosts the video
           * + the current note's drawing overlay + the
           * heartbeat. In playlist mode it's keyed on
           * `session.playKey` so each item advance / restart
           * remounts the player cleanly. Same 16:9 envelope as the
           * clip path so the modal proportions stay stable across
           * all three canvas modes. */
          <div
            ref={envelope.setRef}
            data-testid="feedback-player-note-canvas"
            className="shrink-0 overflow-hidden rounded bg-black mx-auto"
            style={ENVELOPE_STYLE}
            key={session?.playKey ?? "single-note"}
          >
            <TelestratorCanvas
              drawing={displayedDrawing}
              onDrawingChange={() => {
                /* read-only in the modal */
              }}
              width={envelope.envelopeWidth}
              match={effectiveMatch}
              slot={slot}
              videoRef={setVideoEl}
              onHeartbeatKilled={() => setStreamKilled(true)}
              inactive
              hideToolbar
            />
          </div>
        )}

        {/* "What to do next" promotion — PR 4 of the redesign. The
         * line is also templated lower in `UnifiedFeedbackBody`,
         * but lifting it ABOVE the body keeps it adjacent to the
         * media so a player who doesn't scroll past the video
         * still sees the actionable line. Sticky on mobile so it
         * stays visible while the body scrolls. */}
        {!playlistEmpty ? (
          <PlayerActionPreview
            target={target}
            currentNote={currentNote}
            isObservation={isObservationNote && !!observationScene}
          />
        ) : null}

        {/* Playlist session rail — Prev / Pause / Restart / Next +
         * the current item's info. Only in playlist mode. */}
        {session ? (
          <PlaylistSessionRail session={session} controls={playlistControls} />
        ) : null}

        {/* Unified structured-feedback body — Summary + What
         * happened / Why / Next + Additional detail + tags. For
         * clips it collapses to the head row + Description. */}
        {legacyTarget.kind === "clip" ? (
          <UnifiedFeedbackBody
            kind="clip"
            clip={legacyTarget.clip}
            players={players}
          />
        ) : currentNote ? (
          <UnifiedFeedbackBody
            kind="note"
            note={currentNote}
            match={match}
            players={players}
            reviewed={
              legacyTarget.kind === "note"
                ? showReviewedChip
                : !!reviewedNoteIds?.has(currentNote.id)
            }
          />
        ) : null}

        {/* Mark done footer — notes, clips, and playlists. Flips to a
         * disabled "Done ✓" chip on success; the modal stays open per
         * the approved UX. The button copy was "Mark reviewed" /
         * "Reviewed ✓" before the PR 4 follow-up renamed it to match
         * the unified card + the plan's rename map. */}
        {reviewableId ? (
          <div className="mt-1 flex items-center justify-end gap-2">
            {review.error ? (
              <span
                className="text-destructive font-mono text-xs"
                data-testid="feedback-player-review-error"
              >
                Could not save — try again.
              </span>
            ) : null}
            {showReviewedChip ? (
              <span
                data-testid="feedback-player-reviewed-chip"
                className="rounded border border-rule bg-canvas px-2.5 py-1 text-sm font-display text-ink-muted"
                aria-disabled="true"
              >
                Done ✓
              </span>
            ) : (
              <button
                type="button"
                onClick={handleMarkReviewed}
                disabled={review.isPending}
                /* PR 4 follow-up: rename "Mark reviewed" → "Mark done"
                 * to match the unified Review card + the plan's
                 * rename map. Player-facing surface only — coach
                 * surfaces don't mount this modal. Testid retains
                 * the legacy `mark-reviewed` slug so existing E2E
                 * locators still match; only the visible label
                 * changes. */
                data-testid="feedback-player-mark-reviewed"
                className="rounded border border-rule bg-canvas px-2.5 py-1 text-sm text-ink transition-colors hover:bg-field disabled:opacity-50"
              >
                {review.isPending ? "Saving…" : "Mark done"}
              </button>
            )}
          </div>
        ) : null}

        {/* PR 6 — Player response signals (Got it / Need help / I tried
         * this). Only when the host supplies a response index (viewer
         * surfaces only — coach-side preview entry points leave
         * `playerResponses` undefined). */}
        {playerResponses && responseTarget ? (
          <PlayerResponseChips
            target={responseTarget}
            currentSignal={currentResponse?.signal ?? null}
            currentNote={currentResponse?.note ?? ""}
            acknowledgedAt={currentResponse?.acknowledgedAt ?? null}
            onResponded={onReviewUpdated}
          />
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

function CoachFeedbackMediaPlaceholder({ label }: { label: string }) {
  return (
    <div className="coach-feedback-media-frame flex flex-col items-center justify-center gap-1 border border-rule bg-gradient-to-br from-field to-canvas px-6 text-center">
      <strong className="font-display text-base text-ink">{label}</strong>
      <span className="text-sm text-ink-muted">
        The coaching note is still available.
      </span>
    </div>
  );
}

function CoachFeedbackDetail({
  target,
  players,
}: {
  target: Extract<FeedbackPlayerTarget, { kind: "note" | "clip" }>;
  players: Player[];
}) {
  const item = target.kind === "note" ? target.note : target.clip;
  const playerNames = players
    .filter((player) => item.player_ids?.includes(player.id))
    .map((player) => player.display_name);
  const audience = coachAudienceLabel(item.visibility, playerNames);

  if (target.kind === "clip") {
    const description = target.clip.description?.trim();
    return (
      <section className="coach-feedback-detail flex min-w-0 flex-col gap-4">
        {description ? (
          <div>
            <h4 className="font-display text-lg text-ink">Description</h4>
            <p className="mt-1 whitespace-pre-wrap text-base leading-relaxed text-ink">
              {description}
            </p>
          </div>
        ) : null}
        <CoachFeedbackBadges
          tone={null}
          category={target.clip.category}
        />
        <p className="coach-feedback-share rounded px-3 py-3 text-sm text-ink">
          Shared with: {audience}.
        </p>
      </section>
    );
  }

  const note = target.note;
  const summary = note.player_summary?.trim();
  const fullNote = note.body?.trim();
  const happened = note.what_happened?.trim();
  const why = note.why_it_matters?.trim();
  const next = note.what_to_do_next?.trim();
  const referenceVideo = readReferenceVideo(note);
  return (
    <section className="coach-feedback-detail flex min-w-0 flex-col gap-4">
      {summary ? (
        <h3 className="font-display text-xl leading-tight text-ink">
          {summary}
        </h3>
      ) : null}
      {fullNote ? (
        <div>
          <h4 className="font-display text-lg text-ink">Full note</h4>
          <p className="mt-1 whitespace-pre-wrap text-base leading-relaxed text-ink">
            {fullNote}
          </p>
        </div>
      ) : null}
      {happened ? (
        <div>
          <h4 className="font-display text-lg text-ink">What happened</h4>
          <p className="mt-1 whitespace-pre-wrap text-base leading-relaxed text-ink">
            {happened}
          </p>
        </div>
      ) : null}
      {why ? (
        <div>
          <h4 className="font-display text-lg text-ink">Why it matters</h4>
          <p className="mt-1 whitespace-pre-wrap text-base leading-relaxed text-ink">
            {why}
          </p>
        </div>
      ) : null}
      {next ? (
        <div>
          <h4 className="font-display text-lg text-ink">What to do next</h4>
          <p className="mt-1 whitespace-pre-wrap text-base leading-relaxed text-ink">
            {next}
          </p>
        </div>
      ) : null}
      <CoachFeedbackBadges tone={note.note_type} category={note.category} />
      {note.tags?.length ? (
        <div className="flex flex-wrap gap-2" aria-label="Tags">
          {note.tags.map((tag) => (
            <Badge key={tag} variant="muted">
              #{tag}
            </Badge>
          ))}
        </div>
      ) : null}
      <p className="coach-feedback-share rounded px-3 py-3 text-sm text-ink">
        Shared with: {audience}.
      </p>
      {note.coach_private_note?.trim() ? (
        <div className="rounded bg-field px-3 py-3">
          <span className="eyebrow">Coach-only note</span>
          <p className="mt-1 whitespace-pre-wrap text-base text-ink">
            {note.coach_private_note.trim()}
          </p>
        </div>
      ) : null}
      {referenceVideo ? <ReferenceVideoCard video={referenceVideo} /> : null}
    </section>
  );
}

function CoachFeedbackBadges({
  tone,
  category,
}: {
  tone: CoachingNote["note_type"] | null;
  category: string;
}) {
  const toneLabel = tone
    ? tone
        .split("_")
        .map((part) => part[0]?.toUpperCase() + part.slice(1))
        .join(" ")
    : null;
  const categoryLabel = category
    .split("_")
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(" ");
  if (!toneLabel && !categoryLabel) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {toneLabel ? <Badge variant="accent">{toneLabel}</Badge> : null}
      {categoryLabel ? (
        <Badge variant="muted">{categoryLabel}</Badge>
      ) : null}
    </div>
  );
}

function coachAudienceLabel(visibility: string, playerNames: string[]) {
  if (visibility === "coach") return "the coaching staff";
  if (visibility === "team") {
    return "the whole team + their families + the coaching staff";
  }
  if (playerNames.length === 0) {
    return "tagged players + their families + the coaching staff";
  }
  return `${playerNames.join(", ")} + their ${
    playerNames.length === 1 ? "family" : "families"
  } + the coaching staff`;
}

function coachSlotLabel(slot: string) {
  if (slot === "first_half") return "First half";
  if (slot === "second_half") return "Second half";
  if (slot === "full") return "Full match";
  return slot.replaceAll("_", " ");
}

/**
 * Sticky "What to do next" promotion — PR 4 of the Feedback Player
 * UX redesign. Lifts the `what_to_do_next` line out of the unified
 * body and renders it adjacent to the media so a player who only
 * scans the top of the modal still sees the actionable instruction.
 *
 * Sticky-positioned on mobile (the canvas is large; the body below
 * scrolls). Hides entirely when there's no actionable line — the
 * unified body's empty-state copy is enough; we don't want a hollow
 * preview card.
 */
function PlayerActionPreview({
  target,
  currentNote,
  isObservation,
}: {
  target: FeedbackPlayerTarget;
  currentNote: CoachingNote | null;
  isObservation: boolean;
}) {
  let text = "";
  let kicker = "What to do next";
  if (target.kind === "clip") {
    text = (target.clip.description ?? "").trim();
    kicker = "What to watch for";
  } else if (currentNote) {
    text =
      (currentNote.what_to_do_next ?? "").trim() ||
      (currentNote.player_summary ?? "").trim();
    if (isObservation) {
      kicker = "What to do next on the board";
    }
  }
  if (!text) return null;
  return (
    <aside
      className="rounded border border-accent/40 bg-accent/5 px-3 py-2 sm:sticky sm:top-0 sm:z-[1]"
      data-testid="feedback-player-action-preview"
    >
      <p className="text-[0.6rem] font-display uppercase tracking-wider text-accent">
        {kicker}
      </p>
      <p
        className="mt-1 text-sm text-ink"
        data-testid="feedback-player-action-preview-text"
      >
        {text}
      </p>
    </aside>
  );
}
